/**
 * Parse the instant strings this app's own server actually sends — Postgres's `timestamptz` text
 * output, not ISO 8601.
 *
 * `server/src/db/schema/columns.ts`'s `instant()` column is `timestamp(name, { withTimezone: true,
 * mode: 'string' })`. With `mode: 'string'`, drizzle hands back whatever `pg`'s text protocol
 * produced for the column verbatim, with no reformatting — confirmed both by reading drizzle-orm's
 * own source and against this repo's real running server: `POST /api/appointments` answers
 * `"startsAt":"2026-08-18 15:00:00+00"` (space-separated, no `T`, offset as bare `+00` with no colon
 * and no minutes), and `createdAt`/`updatedAt` the same shape with microsecond fractional seconds:
 * `"2026-08-18 17:24:01.853581+00"`. Neither has a `Z` suffix or a `T` separator.
 *
 * Every date/time render in this feature previously called `new Date(iso)` directly on these
 * strings, which happened to work in V8 (Chrome, Node) because V8's `Date` constructor accepts a
 * wide range of non-standard formats as a legacy/implementation extension — but the ECMA-262 Date
 * Time String Format only guarantees parsing for the `YYYY-MM-DDTHH:mm:ss.sssZ` shape (and its
 * shorter prefixes); anything else is explicitly implementation-defined per spec, not merely
 * "usually works." That is not a hypothetical gap for this project: iPad-based clinical workstations
 * (a real deployment target, `CLAUDE.md`) run Safari/WebKit, whose `Date` parser is stricter about
 * non-standard formats than V8's — a WebKit engine that returned `Invalid Date` for this exact string
 * would have made every appointment time on this page silently unrenderable, with no server-side
 * signal at all that anything was wrong.
 *
 * This function is the one place a raw server timestamp string is turned into a `Date` — every
 * caller in `formatDateTime.ts` and `datetimeLocalInput.ts` goes through it rather than calling
 * `new Date(...)` directly on server-sourced data, so this explicit, tested parse is the only thing
 * standing between the wire format and a rendered page, on every engine.
 */

const SERVER_TIMESTAMP =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}(?::?\d{2})?)?$/;

function offsetMinutes(offset: string): number {
  if (offset === 'Z') return 0;
  const sign = offset.startsWith('-') ? -1 : 1;
  const digits = offset.slice(1).replace(':', '');
  const hours = Number(digits.slice(0, 2));
  const minutes = digits.length > 2 ? Number(digits.slice(2, 4)) : 0;
  return sign * (hours * 60 + minutes);
}

/**
 * Parse a server-sourced instant string (Postgres `timestamptz` text, or a `Z`-suffixed ISO string —
 * accepted too, so a test fixture built with `.toISOString()` and a future server-side format change
 * both still parse through this same explicit path rather than needing a second one). Throws on
 * anything else: a value that does not match either shape is corrupt data, not a business outcome to
 * silently coerce into `Invalid Date` and render as "NaN:NaN" three components down.
 */
export function parseServerInstant(value: string): Date {
  const match = SERVER_TIMESTAMP.exec(value.trim());
  if (match === null) {
    throw new Error(`could not parse server timestamp: ${JSON.stringify(value)}`);
  }
  const [, year, month, day, hour, minute, second, fraction, offset] = match;
  // Every capture group but the fractional-seconds and offset ones is required by the regex, so
  // these are always defined when `match` is non-null — the `?? ''`/`?? '0'` fallbacks exist only to
  // satisfy the type checker's `string | undefined` inference over a regex match array, not because
  // any of these can genuinely be missing here.
  // Truncated, not rounded: Postgres's own microsecond fraction (e.g. `.853581`) is more precise
  // than a JS `Date`'s millisecond resolution, and truncating to the whole millisecond it's already
  // within is the same thing every lower-precision timestamp representation does when narrowing —
  // rounding up would report a millisecond value the original instant hadn't actually reached yet.
  const milliseconds = fraction === undefined ? 0 : Math.floor(Number(`0.${fraction}`) * 1000);
  const minutesFromUtc = offset === undefined ? 0 : offsetMinutes(offset);

  const utcMillis =
    Date.UTC(
      Number(year ?? '0'),
      Number(month ?? '1') - 1,
      Number(day ?? '1'),
      Number(hour ?? '0'),
      Number(minute ?? '0'),
      Number(second ?? '0'),
      milliseconds,
    ) -
    minutesFromUtc * 60_000;

  return new Date(utcMillis);
}
